import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { SettingsMode } from './enum/SettingsMode.enum';
import { sendHttpRequest } from '../../utils/network.service';
import './integration-card.css';
import { QCard, QCardContent, QTypography, QButton, QTextField } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';
import { MDM_INTEGRATION_SETTINGS_ID } from '../shared/constants';
import { StyledIntegrationCard } from '../shared/StyledIntegrationCard';
import { MdmIntegrationCardPauseButton, MdmIntegrationCardStatus } from '../shared/MdmIntegrationStatusControl';
import { isPaused, MdmIntegrationStatus } from '../shared/mdmIntegrationStatus';

interface IvantiIntegrationCardProps {
  usernamePlaceholder: string;
  passwordPlaceholder: string;
  lastSync: string;
  isAdmin: boolean;
  mode: { [key: string]: SettingsMode };
  setMode: React.Dispatch<React.SetStateAction<{ [key: string]: SettingsMode }>>;
  mutate: () => void;
  /** Undefined until the integration is configured. */
  status?: MdmIntegrationStatus;
  onStatusChange: () => void;
}

export const IvantiIntegrationCard = ({
  usernamePlaceholder,
  passwordPlaceholder,
  lastSync,
  isAdmin,
  mode,
  setMode,
  mutate,
  status,
  onStatusChange,
}: IvantiIntegrationCardProps) => {
  const isEditMode = mode.ivanti === SettingsMode.EDIT;
  const isReadMode = mode.ivanti === SettingsMode.READ;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [IvantiFormErrors, setIvantiFormErrors] = useState({
    username: false,
    password: false,
    validated: false,
  });
  const [connectionFailed, setConnectionFailed] = useState(false);
  const lastSyncTimestamp = lastSync ? new Date(parseInt(lastSync, 10)).toLocaleString() : 'Never';

  const resetInputValues = () => {
    setUsername('');
    setPassword('');
    setConnectionFailed(false);
    setIvantiFormErrors({ username: false, password: false, validated: false });
    setMode({ ...mode, ivanti: SettingsMode.READ });
  };

  function validateIvantiSettings() {
    setIvantiFormErrors({
      username: !username.length,
      password: !password.length,
      validated: true,
    });
  }

  const syncNow = async () => {
    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.IVANTI}/sync`,
      method: 'POST',
      body: {},
      successMessage: 'Ivanti sync initiated successfully',
      errorMessage: 'Error initiating Ivanti sync',
      enqueueSnackbar,
    });
  };

  const updateIvantiSettings = async () => {
    validateIvantiSettings();

    if (!username.length || !password.length) {
      return;
    }

    const existingSettings = username.length || password.length;
    let path = `${process.env.ORGANIZATION_SERVICE_URL}`;
    path += existingSettings ? `/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.IVANTI}` : '/organizations/mdm-integrations';
    const method = existingSettings ? 'PUT' : 'POST';
    const body = existingSettings
      ? {}
      : { mdmIntegrationId: MDM_INTEGRATION_SETTINGS_ID.IVANTI, settings: { username: username, password: password } };

    if (existingSettings) {
      const settings = {};
      if (username.length) {
        Object.assign(settings, { ...settings, username });
      }
      if (password.length) {
        Object.assign(settings, { ...settings, password });
      }

      Object.assign(body, { settings });
    }

    await sendHttpRequest({
      path,
      method,
      body,
      successMessage: 'Ivanti settings updated successfully',
      errorMessage: 'Error updating Ivanti settings',
      enqueueSnackbar,
    });

    // A paused integration refuses to sync, and saving credentials doesn't resume it.
    if (!isPaused(status)) await syncNow();

    mutate();
    // A first save adds the integration to the status list, which shows its Pause button.
    onStatusChange();
    resetInputValues();
  };

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  if (!flags?.ivantiIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  return (
    <StyledIntegrationCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img src="/logo/ivanti.png" className="logo" />
          Ivanti Neurons for MDM
        </StyledQTypography>
        <KwFormErrorMessage error={Boolean(connectionFailed)} reason="We couldn't make a connection with the information provided" />
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Configure Q-scout’s connection to the Ivanti Neurons API to import Mobile Device and App Information from your Ivanti Neurons for
          MDM tenant. The integration will pull data from your tenant’s Default Space.{' '}
          <a
            href="https://help.ivanti.com/mi/help/en_us/cld/admin/ivanti/120/all/en-us/About%20Cloud.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <StyledQTypography variant="h5Bold">Username</StyledQTypography>
            <QTextField
              placeholder={usernamePlaceholder}
              onChange={e => setUsername(e.target.value)}
              error={Boolean(IvantiFormErrors.username)}
            />
            <KwFormErrorMessage error={Boolean(IvantiFormErrors.username)} reason="Enter the Ivanti username" />

            <StyledQTypography variant="h5Bold">Password</StyledQTypography>
            <QTextField
              placeholder={passwordPlaceholder}
              onChange={e => setPassword(e.target.value)}
              error={Boolean(IvantiFormErrors.password)}
            />
            <KwFormErrorMessage error={Boolean(IvantiFormErrors.password)} reason="Enter the Ivanti password" />
          </>
        ) : (
          <>
            <StyledQTypography variant="h5Bold">Username</StyledQTypography>
            <StyledQTypography>{usernamePlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Password</StyledQTypography>
            <StyledQTypography>{passwordPlaceholder}</StyledQTypography>
          </>
        )}

        <MdmIntegrationCardStatus status={status} />

        <div className="action-wrapper">
          {isAdmin && isEditMode ? (
            <>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateIvantiSettings}>
                Save
              </QButton>
            </>
          ) : (
            <>
              {isAdmin && isReadMode ? (
                <>
                  <div className="sync-action-wrapper">
                    <QButton
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => syncNow()}
                      disabled={!usernamePlaceholder || !passwordPlaceholder || isPaused(status)}
                    >
                      <img src="icons/sync-now.svg" className="sync-now-icon" />
                      <span className="sync-now-button-text">Sync Now</span>
                    </QButton>
                    <QTypography sx={{ color: '#757575' }}>Last synced: {lastSyncTimestamp}</QTypography>
                  </div>
                  <MdmIntegrationCardPauseButton label="Ivanti" status={status} onChange={onStatusChange} />
                  <QButton variant="filled" onClick={() => setMode({ ...mode, ivanti: SettingsMode.EDIT })}>
                    Edit
                  </QButton>
                </>
              ) : (
                <QButton disabled variant="filled">
                  Save
                </QButton>
              )}
            </>
          )}
        </div>
      </QCardContent>
    </StyledIntegrationCard>
  );
};

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;
